import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gift, Award, RefreshCw } from "lucide-react";

export const nzDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric", timeZone: "Pacific/Auckland" }) : "—";

const STATUS = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-900" },
  canceling: { label: "Cancelling", cls: "bg-amber-100 text-amber-900" },
  past_due: { label: "Payment failed", cls: "bg-red-100 text-red-800" },
  canceled: { label: "Cancelled", cls: "bg-slate-200 text-slate-700" },
  none: { label: "Not joined", cls: "bg-slate-100 text-slate-600" },
};

export const FreeNotice = ({ freeUntil }) => (
  <div className="rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-sm text-amber-900 font-semibold" data-testid="fiver-free-notice">
    FREE until {nzDate(freeUntil)}, card checked now, nothing charged until then, then NZ$5/month.
  </div>
);

function JoinPanel({ plan, onJoin, busy }) {
  return (
    <div className="space-y-3">
      <div className="font-display font-black text-lg text-slate-900">Fiver Deal · NZ$5/month · 28 adverts</div>
      {plan.in_free_period && <FreeNotice freeUntil={plan.free_until} />}
      <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4">
        <li>28 credits every month (1 credit = 1 advert for 30 days). Monthly credits don&apos;t roll over.</li>
        <li>NZ$5 price locked for 12 months. Cancel any time — it ends after the month you&apos;ve paid for. No refunds.</li>
        {plan.in_free_period && <li>Join before {nzDate(plan.free_until)} to get a <b>Founding Seller</b> badge.</li>}
      </ul>
      <Button onClick={onJoin} disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 text-white h-10" data-testid="fiver-join-btn">
        <Gift className="w-4 h-4 mr-1.5" />{busy ? "Opening Stripe…" : plan.in_free_period ? "Join free — add card" : "Join the Fiver Deal · NZ$5/month"}
      </Button>
    </div>
  );
}

function ActivePanel({ plan, onCancel, busy }) {
  const billingLabel = plan.plan_status === "canceling" ? "Plan ends" : plan.plan_trialing ? "First charge" : "Next billing";
  return (
    <div className="space-y-2 text-sm">
      {plan.plan_status === "past_due" && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-red-800 text-xs" data-testid="plan-past-due">Your last payment failed. Stripe will retry — please check the card on file. You can&apos;t post new adverts until it&apos;s paid.</div>}
      <div className="grid grid-cols-2 gap-2">
        <div><div className="text-[10px] uppercase tracking-widest text-slate-500">{billingLabel}</div><div className="font-semibold" data-testid="plan-next-billing">{nzDate(plan.plan_period_end)}</div></div>
        <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Price locked until</div><div className="font-semibold" data-testid="plan-price-lock">{nzDate(plan.price_locked_until)}</div></div>
        <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Monthly credits</div><div className="font-display font-black text-xl text-emerald-900" data-testid="plan-monthly-credits">{plan.monthly_credits}<span className="text-slate-400 text-sm"> / 28</span></div></div>
        <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Bonus credits</div><div className="font-display font-black text-xl text-amber-600" data-testid="plan-bonus-credits">{plan.bonus_credits}</div></div>
      </div>
      {plan.credits_cycle_end && <div className="text-xs text-slate-500">Monthly credits reset to 28 on {nzDate(plan.credits_cycle_end)}.</div>}
      {plan.plan_status === "active" && (
        <Button variant="outline" onClick={onCancel} disabled={busy} className="w-full h-9 text-xs" data-testid="plan-cancel-btn">Cancel plan (ends after this paid month)</Button>
      )}
      {plan.plan_status === "canceling" && <div className="text-xs text-amber-800" data-testid="plan-canceling-note">Cancelled — you can keep posting until {nzDate(plan.plan_period_end)}. Adverts are hidden after that.</div>}
    </div>
  );
}

export default function PlanCard({ plan, onChange }) {
  const [busy, setBusy] = useState(false);
  const status = STATUS[plan.plan_status] || STATUS.none;
  const joined = ["active", "canceling", "past_due"].includes(plan.plan_status);
  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/fiver", { origin_url: window.location.origin });
      if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel your Fiver Deal? It stays active until the end of the month you've paid for. No refunds.")) return;
    setBusy(true);
    try { await api.post("/subscription/cancel"); toast.success("Plan cancelled — active until the end of this paid month"); onChange(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sync = async () => {
    try { await api.post("/plan/sync"); onChange(); toast.success("Plan refreshed"); } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-white" data-testid="plan-card">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="text-xs font-black uppercase tracking-widest text-amber-700 flex-1">Your plan</div>
          {plan.founding_seller && <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-fuchsia-100 text-fuchsia-800 rounded-full px-2 py-0.5" data-testid="plan-founding-badge"><Award className="w-3 h-3" />Founding Seller</span>}
          <span className={`text-[10px] font-black uppercase tracking-wider rounded-full px-2 py-0.5 ${status.cls}`} data-testid="plan-status">{status.label}</span>
          {joined && <button onClick={sync} title="Refresh plan from Stripe" className="text-slate-500 hover:text-slate-800" data-testid="plan-sync-btn"><RefreshCw className="w-3.5 h-3.5" /></button>}
        </div>
        {joined ? <ActivePanel plan={plan} onCancel={cancel} busy={busy} /> : <JoinPanel plan={plan} onJoin={join} busy={busy} />}
      </CardContent>
    </Card>
  );
}
